<template>
  <div
    v-if="topic"
    class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    @click.self="$emit('close')"
  >
    <div
      class="bg-white rounded-xl shadow-xl max-w-2xl w-full overflow-hidden"
      role="dialog"
      aria-labelledby="trend-modal-title"
    >
      <!-- Header -->
      <div
        class="px-6 py-4 border-b border-gray-200 flex items-center justify-between"
      >
        <h2 id="trend-modal-title" class="text-lg font-semibold text-gray-900">
          {{ topic.topic }} — Daily mentions
        </h2>
        <button
          type="button"
          class="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
          aria-label="Close"
          @click="$emit('close')"
        >
          <svg
            class="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <!-- Chart -->
      <div class="px-6 py-6">
        <div v-if="!sortedData.length" class="text-center py-12 text-gray-500">
          No trend data available
        </div>
        <div v-else class="space-y-4">
          <!-- Chart: single SVG with chart, grid, y-axis, x-axis for proper alignment -->
          <div class="flex-1 min-w-0 pb-2">
            <svg
              :viewBox="`0 0 ${chartWidth} ${chartHeight + 48}`"
              class="w-full text-violet-500 block"
              style="height: 12rem"
              preserveAspectRatio="xMidYMid meet"
            >
              <!-- Grid lines at actual value positions (max, mid, min) -->
              <g v-for="tick in yTicks" :key="tick.value" class="text-gray-200">
                <line
                  :y1="tick.y"
                  :y2="tick.y"
                  :x1="plotLeft"
                  :x2="plotRight"
                  stroke="currentColor"
                  stroke-width="0.5"
                  stroke-dasharray="4 2"
                />
              </g>
              <!-- Y-axis labels inside SVG at correct y positions -->
              <text
                v-for="tick in yTicks"
                :key="'lbl-' + tick.value"
                :x="plotLeft - 8"
                :y="tick.y"
                text-anchor="end"
                dominant-baseline="middle"
                class="text-gray-500 fill-current"
                style="font-size: 11px; font-family: ui-monospace, monospace"
              >
                {{ tick.value }}
              </text>
              <!-- Line + points -->
              <polyline
                :points="pathPoints"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <g v-for="(pt, i) in chartPoints" :key="i">
                <circle :cx="pt.x" :cy="pt.y" r="4" fill="currentColor" />
              </g>
              <!-- X-axis labels (extra bottom space so dates aren't cut off) -->
              <text
                v-for="(lp, i) in labelPoints"
                :key="i"
                :x="lp.x"
                :y="chartHeight + 24"
                text-anchor="middle"
                dominant-baseline="hanging"
                class="text-gray-500 fill-current"
                style="font-size: 11px"
              >
                {{ formatDate(lp.processed_date) }}
              </text>
            </svg>
          </div>
          <!-- Data table -->
          <div class="mt-4 border border-gray-200 rounded-lg overflow-hidden">
            <table class="w-full text-sm">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-4 py-2 text-left font-medium text-gray-700">
                    Date
                  </th>
                  <th class="px-4 py-2 text-right font-medium text-gray-700">
                    Mentions
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr
                  v-for="d in sortedData"
                  :key="d.processed_date"
                  class="hover:bg-gray-50"
                >
                  <td class="px-4 py-2 text-gray-700">
                    {{ formatDate(d.processed_date) }}
                  </td>
                  <td class="px-4 py-2 text-right font-medium text-gray-900">
                    {{ d.count }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  computed,
  PropType,
  onMounted,
  onUnmounted,
} from "vue";
import type { TrendingTopic } from "@/stores/insights";

const PADDING = { top: 12, right: 12, bottom: 12, left: 12 };
const Y_AXIS_WIDTH = 28; // Space for y-axis labels (left of plot)
const LABEL_MARGIN = 24; // Extra space so first/last date labels don't get cut off
const CHART_WIDTH = 400 + Y_AXIS_WIDTH + LABEL_MARGIN * 2;
const CHART_HEIGHT = 160;

export default defineComponent({
  name: "TrendDetailModal",
  props: {
    topic: {
      type: Object as PropType<TrendingTopic | null>,
      default: null,
    },
  },
  emits: ["close"],
  setup(props, { emit }) {
    const sortedData = computed(() => {
      const data = props.topic?.trend_data ?? [];
      return [...data].sort((a, b) =>
        (a.processed_date || "").localeCompare(b.processed_date || "")
      );
    });

    const yMin = computed(() => {
      const counts = sortedData.value.map((d) => d.count);
      return counts.length ? Math.min(...counts) : 0;
    });
    const yMax = computed(() => {
      const counts = sortedData.value.map((d) => d.count);
      return counts.length ? Math.max(...counts) : 1;
    });

    const plotHeight = computed(
      () => CHART_HEIGHT - PADDING.top - PADDING.bottom
    );
    const plotLeft = PADDING.left + Y_AXIS_WIDTH + LABEL_MARGIN;
    const plotRight = CHART_WIDTH - PADDING.right - LABEL_MARGIN;

    const yTicks = computed(() => {
      const min = yMin.value;
      const max = yMax.value;
      const h = plotHeight.value;
      const range = max - min || 1;
      const yFor = (v: number) => PADDING.top + h - ((v - min) / range) * h;
      if (min === max) {
        return [{ value: max, y: yFor(max) }];
      }
      const mid = Math.round((min + max) / 2);
      return [
        { value: max, y: yFor(max) },
        { value: mid, y: yFor(mid) },
        { value: min, y: yFor(min) },
      ];
    });

    const chartPoints = computed(() => {
      const data = sortedData.value;
      if (!data.length) return [];
      const points = data.length === 1 ? [data[0], data[0]] : data;
      const range = yMax.value - yMin.value || 1;
      const w = plotRight - plotLeft;
      const h = plotHeight.value;
      const stepX = points.length > 1 ? w / (points.length - 1) : 0;
      return points.map((d, i) => ({
        x: plotLeft + i * stepX,
        y: PADDING.top + h - ((d.count - yMin.value) / range) * h,
        ...d,
      }));
    });

    const pathPoints = computed(() =>
      chartPoints.value.map((p) => `${p.x},${p.y}`).join(" ")
    );

    const labelPoints = computed(() => {
      const data = sortedData.value;
      if (!data.length) return [];
      const w = plotRight - plotLeft;
      const stepX = data.length > 1 ? w / (data.length - 1) : 0;
      return data.map((d, i) => ({
        x: plotLeft + i * stepX,
        processed_date: d.processed_date,
      }));
    });

    const formatDate = (d: string) => {
      if (!d) return "—";
      const [, m, day] = d.split("-");
      const month = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ][parseInt(m || "1", 10) - 1];
      return `${month} ${parseInt(day || "0", 10)}`;
    };

    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && props.topic) emit("close");
    };
    onMounted(() => window.addEventListener("keydown", handleKeydown));
    onUnmounted(() => window.removeEventListener("keydown", handleKeydown));

    return {
      sortedData,
      yMin,
      yMax,
      yTicks,
      plotLeft,
      plotRight,
      chartPoints,
      pathPoints,
      labelPoints,
      formatDate,
      chartWidth: CHART_WIDTH,
      chartHeight: CHART_HEIGHT,
      padding: PADDING,
      labelMargin: LABEL_MARGIN,
    };
  },
});
</script>
